// Fix FL (2026-10-09): "en la parte de servicios de alquileres necesitamos también la parte de
// facturas para poder hacer la validación (…) el equivalente que tenemos en compras materiales
// también en la parte de servicios" (CEN). Las facturas se vinculan a cada certificación (CRT):
// la del servicio general y la de cada período del alquiler. Montos con IVA incluido.
import { useState, useRef } from "react";
import {
  X, Loader2, Receipt, BadgeCheck, AlertTriangle, Paperclip, UploadCloud, Zap, Trash2, Plus,
  FileUp, Files, Unlink, Eye, Download, Lock, Home, Search,
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatPYG, formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";
import { Modal } from "./SharedComponents";
import { roleAllowed } from "../../utils/roles";
import { useFiltrosServicios, BarraFiltrosServicios } from "./FiltrosServicios";   // Fix FV (2026-10-13)
import { BadgeCierre, VinculacionCerradaInfo, CerrarVinculacionModal } from "./VinculacionCerrada";   // Fix GS (2026-10-17)

const MAX_FILE_MB = 10;
let draftSeq = 0;
const newDraft = (extra = {}) => ({
  key: `s${++draftSeq}`, file: null, file_name: "", invoice_number: "", invoice_date: "", invoice_total: "",
  timbrado: "", tipo_documento: "Física", venc_timbrado: "", analyzing: false, analyzed: false, error: "", obs: "", ...extra,
});
const readAsDataURL = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = (e) => resolve(e.target.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});
const draftToInvoice = (d) => ({
  invoice_number: (d.invoice_number || "").trim(), invoice_date: d.invoice_date || "",
  invoice_total: Number(d.invoice_total || 0), timbrado: d.timbrado || "", tipo_documento: d.tipo_documento || "Física",
  venc_timbrado: d.venc_timbrado || "", doc_name: d.file_name || "", items: [],
});

// Facturas de la certificación (las cargadas antes en el modal de certificar cuentan como una).
export const crtInvoices = (crt) => {
  if (Array.isArray(crt?.invoices) && crt.invoices.length) return crt.invoices;
  if (crt?.invoice_number) return [{ invoice_id: "legacy-1", invoice_number: crt.invoice_number, invoice_total: Number(crt.invoice_total || 0) || Number(crt.total || 0), items: [] }];
  return [];
};
export const crtCoverage = (crt, extra = []) => {
  const invs = [...crtInvoices(crt), ...extra];
  const invoiced = invs.reduce((s, i) => s + Number(i.invoice_total || 0), 0);
  const base = Number(crt?.total || 0);
  return {
    invoices_count: invs.length, invoiced_total: invoiced, crt_total: base,
    pending_total: Math.max(base - invoiced, 0), over: invoiced > base + 1,
    complete: invs.length > 0 && (invoiced >= base - 1 || !!crt?.vinculacion_facturas_cerrada),
  };
};

function DraftCard({ draft, index, onChange, onRemove }) {
  const update = (patch) => onChange({ ...draft, ...patch });
  const inp = "w-full border border-gray-300 rounded-lg px-2.5 py-2 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:outline-none";
  return (
    <div className="rounded-xl border border-[#2E6DA4]/30 bg-white overflow-hidden" data-testid={`svc-invoice-draft-${index}`}>
      <div className="bg-[#2E6DA4]/8 px-3 py-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-[10px] font-bold bg-[#2E6DA4] text-white rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">{index + 1}</span>
          <p className="text-xs font-semibold text-[#2E6DA4] truncate">
            {draft.invoice_number || "Factura sin número"}
            {draft.file_name ? <span className="text-gray-500 font-normal"> · {draft.file_name}</span> : null}
          </p>
          {draft.file && (
            <button onClick={() => window.open(URL.createObjectURL(draft.file), "_blank")} title="Previsualizar el archivo"
              className="text-gray-400 hover:text-[#2E6DA4] flex-shrink-0"><Eye size={13} /></button>
          )}
          {draft.analyzing && <Loader2 size={12} className="animate-spin text-[#2E6DA4]" />}
        </div>
        <button onClick={onRemove} className="text-gray-400 hover:text-red-500 flex-shrink-0" title="Quitar esta factura"><Trash2 size={13} /></button>
      </div>
      {draft.error && <p className="px-3 py-1.5 text-[11px] text-red-700 bg-red-50 border-b border-red-100">{draft.error}</p>}
      {draft.obs && <p className="px-3 py-1.5 text-[11px] text-amber-800 bg-amber-50 border-b border-amber-100">IA: {draft.obs}</p>}
      <div className="p-3 space-y-2">
        <div className="grid grid-cols-3 gap-2">
          <div><label className="block text-[10px] font-semibold text-gray-600 mb-1">N° de Factura *</label>
            <input value={draft.invoice_number} onChange={e => update({ invoice_number: e.target.value })} placeholder="001-001-0001234" className={`${inp} font-mono`} data-testid={`svc-inv-number-${index}`} /></div>
          <div><label className="block text-[10px] font-semibold text-gray-600 mb-1">Fecha Factura</label>
            <input type="date" value={draft.invoice_date} onChange={e => update({ invoice_date: e.target.value })} className={inp} data-testid={`svc-inv-date-${index}`} /></div>
          <div><label className="block text-[10px] font-semibold text-gray-600 mb-1">Total Factura (Gs., IVA incluido)</label>
            <input type="number" value={draft.invoice_total} onChange={e => update({ invoice_total: e.target.value })} placeholder="0" className={inp} data-testid={`svc-inv-total-${index}`} /></div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div><label className="block text-[10px] font-semibold text-gray-600 mb-1">Timbrado</label>
            <input value={draft.timbrado} onChange={e => update({ timbrado: e.target.value })} placeholder="12345678" className={inp} data-testid={`svc-inv-timbrado-${index}`} /></div>
          <div><label className="block text-[10px] font-semibold text-gray-600 mb-1">Tipo de Documento</label>
            <select value={draft.tipo_documento} onChange={e => update({ tipo_documento: e.target.value })} className={`${inp} bg-white`}>
              <option value="Física">Física</option><option value="Electrónica">Electrónica</option>
            </select></div>
          <div><label className="block text-[10px] font-semibold text-gray-600 mb-1">Venc. Timbrado</label>
            <input type="date" value={draft.venc_timbrado} onChange={e => update({ venc_timbrado: e.target.value })} className={inp} /></div>
        </div>
      </div>
    </div>
  );
}

export function ServiciosFacturasTab({ certifications, loading, onRefresh, userRole, svcOrders = [], filtros }) {
  const [modal, setModal] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [linked, setLinked] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [linking, setLinking] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [cerrarCRT, setCerrarCRT] = useState(null);   // Fix GS (2026-10-17)
  const [attachingId, setAttachingId] = useState(null);
  const fl = useFiltrosServicios(filtros);   // Fix FV (2026-10-13): los filtros de Materiales
  const filesRef = useRef(null);
  const attachRef = useRef(null);

  const canMatch = roleAllowed(userRole, ["Superadministrador", "Administrador", "Compras", "Contador", "Gerente Tecnico"]);
  const canUnlink = roleAllowed(userRole, ["Superadministrador", "Administrador", "Jefe de Compras", "Compras"]);
  // El documento se sirve por el mismo endpoint de las facturas de la OC (clave = id de la certificación).
  const docUrl = (crtId, invId, inline) => `${API}/purchases/oc-from-pf/${crtId}/invoices/${invId}/document${inline ? "?inline=1" : ""}`;

  // Fix FV (2026-10-13): la certificación toma la obra y el presupuesto de su OTS.
  const otsPorId = Object.fromEntries((svcOrders || []).map(o => [o._id, o]));
  const refOTS = (c) => otsPorId[c.ots_id];
  const estadoFactura = (c) => crtCoverage(c).complete ? "vinculada" : "pendiente";
  const vigentes = (certifications || []).filter(c => !c.cancelled_at);
  const activas = fl.filtrar(vigentes, { ref: refOTS, estado: estadoFactura });
  const byDateDesc = (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0);
  const pendientes = activas.filter(c => !crtCoverage(c).complete).sort(byDateDesc);
  const facturadas = activas.filter(c => crtCoverage(c).complete).sort(byDateDesc);

  const fetchLinked = async (crtId) => {
    try {
      const r = await axios.get(`${API}/purchases/service-certifications/${crtId}/invoices`, { withCredentials: true });
      setLinked(r.data?.invoices || []);
    } catch { setLinked([]); }
  };
  const openModal = (crt) => { setModal(crt); setDrafts([]); setLinked(crtInvoices(crt)); fetchLinked(crt._id); };
  const live = modal ? crtCoverage({ ...modal, invoices: linked, invoice_number: linked.length ? modal.invoice_number : "" }, drafts.map(draftToInvoice)) : null;

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    const ok = [];
    files.forEach(f => {
      if (f.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`${f.name} supera ${MAX_FILE_MB}MB — no se agregó.`); return; }
      ok.push(newDraft({ file: f, file_name: f.name }));
    });
    if (ok.length) { setDrafts(p => [...p, ...ok]); toast.success(`${ok.length} factura(s) agregada(s). Analizá con IA o completá los datos.`); }
  };

  const analizar = async () => {
    const targets = drafts.filter(d => d.file && !d.analyzed);
    if (!targets.length) return;
    setAnalyzing(true);
    for (const t of targets) {
      setDrafts(p => p.map(d => d.key === t.key ? { ...d, analyzing: true, error: "" } : d));
      try {
        const file_data = await readAsDataURL(t.file);
        const r = await axios.post(`${API}/purchases/service-certifications/${modal._id}/ai-extract-invoices`,
          { files: [{ file_data, file_name: t.file_name, file_type: t.file.type }] }, { withCredentials: true });
        const res = (r.data?.results || [])[0] || {};
        setDrafts(p => p.map(d => d.key !== t.key ? d : (!res.ok ? { ...d, analyzing: false, analyzed: true, error: res.error || "No se pudo analizar" } : {
          ...d, analyzing: false, analyzed: true, error: "",
          invoice_number: res.nro_factura || d.invoice_number, invoice_date: res.fecha_factura || d.invoice_date,
          invoice_total: res.total != null ? String(res.total) : d.invoice_total, timbrado: res.timbrado || d.timbrado,
          venc_timbrado: res.venc_timbrado || d.venc_timbrado,
          tipo_documento: res.tipo_documento === "Electrónica" ? "Electrónica" : (d.tipo_documento || "Física"), obs: res.obs || "",
        })));
      } catch (err) {
        setDrafts(p => p.map(d => d.key === t.key ? { ...d, analyzing: false, analyzed: true, error: err.response?.data?.detail || "Error al analizar la factura" } : d));
      }
    }
    setAnalyzing(false);
    toast.success("Análisis finalizado. Revisá y corregí antes de confirmar.");
  };

  const vincular = async () => {
    const valid = drafts.filter(d => (d.invoice_number || "").trim());
    if (!valid.length) { toast.error("Ingresá el número de al menos una factura"); return; }
    if (valid.length !== drafts.length) { toast.error("Hay facturas sin número: completalas o quitalas"); return; }
    setLinking(true);
    try {
      const r = await axios.post(`${API}/purchases/service-certifications/${modal._id}/link-invoices`,
        { invoices: valid.map(draftToInvoice) }, { withCredentials: true });
      const creadas = (r.data?.invoices || []).slice(-valid.length);
      const conArchivo = valid.map((d, i) => ({ d, inv: creadas[i] })).filter(x => x.d.file && x.inv);
      if (conArchivo.length) {
        const res = await Promise.allSettled(conArchivo.map(async ({ d, inv }) => {
          const file_data = await readAsDataURL(d.file);
          await axios.put(`${API}/purchases/service-certifications/${modal._id}/invoices/${inv.invoice_id}/document`,
            { file_data, file_name: d.file_name, file_type: d.file.type }, { withCredentials: true });
        }));
        const fallos = res.filter(x => x.status === "rejected").length;
        if (fallos) toast.error(`${fallos} documento(s) no se pudieron adjuntar. Podés reintentar desde "Agregar / Ver facturas".`);
      }
      toast.success(valid.length === 1 ? "Factura vinculada a la certificación" : `${valid.length} facturas vinculadas a la certificación`);
      setModal(null); onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al vincular");
    } finally { setLinking(false); }
  };

  const desvincular = async (inv) => {
    if (!window.confirm(`¿Desvincular la factura ${inv.invoice_number} de ${modal.number}? Queda registrado en el historial.`)) return;
    setBusyId(inv.invoice_id);
    try {
      await axios.delete(`${API}/purchases/service-certifications/${modal._id}/invoices/${inv.invoice_id || inv.invoice_number}`, { withCredentials: true });
      toast.success("Factura desvinculada"); await fetchLinked(modal._id); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al desvincular"); }
    finally { setBusyId(null); }
  };

  const onAttach = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !attachingId || !modal) { setAttachingId(null); return; }
    if (file.size > MAX_FILE_MB * 1024 * 1024) { toast.error(`El archivo supera ${MAX_FILE_MB}MB.`); setAttachingId(null); return; }
    try {
      const file_data = await readAsDataURL(file);
      await axios.put(`${API}/purchases/service-certifications/${modal._id}/invoices/${attachingId}/document`,
        { file_data, file_name: file.name, file_type: file.type }, { withCredentials: true });
      toast.success("Documento adjuntado"); await fetchLinked(modal._id); onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al adjuntar el documento"); }
    finally { setAttachingId(null); }
  };

  const vinculacion = async (crt, cerrar, motivo = "") => {
    // Fix GS (2026-10-17): el cierre se pide en su ventana (CerrarVinculacionModal), con la diferencia a la vista.
    if (cerrar && !motivo) { setCerrarCRT(crt); return; }
    if (!cerrar && !window.confirm(`¿Reabrir la vinculación de facturas de ${crt.number}?`)) return;
    setBusyId(crt._id);
    try {
      await axios.put(`${API}/purchases/service-certifications/${crt._id}/vinculacion-facturas`, { cerrar, motivo }, { withCredentials: true });
      toast.success(cerrar ? `Vinculación cerrada: ${crt.number} queda facturada` : `Vinculación reabierta: ${crt.number}`);
      setCerrarCRT(null);
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo actualizar la vinculación"); }
    finally { setBusyId(null); }
  };

  const card = (crt, pend) => {
    const cov = crtCoverage(crt);
    const invs = crtInvoices(crt);
    return (
      <div key={crt._id} className={`bg-white rounded-xl border p-4 shadow-sm ${pend ? "border-gray-200/60" : "border-emerald-200/60"}`} data-testid={pend ? "svc-factura-card" : "svc-facturada-card"}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-bold text-gray-900 text-sm font-mono">{crt.number}</p>
              <span className="text-[11px] text-gray-500 font-mono">OTS {crt.ots_number}</span>
              {crt.categoria === "alquiler" && <span className="text-[10px] font-bold bg-orange-50 text-orange-600 border border-orange-200 px-2 py-0.5 rounded-full uppercase flex items-center gap-1"><Home size={9} /> Alquiler</span>}
              {invs.length > 1 && <span className="text-[10px] bg-[#2E6DA4]/10 text-[#2E6DA4] border border-[#2E6DA4]/20 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"><Files size={10} /> {invs.length} facturas</span>}
              {pend && invs.length > 0 && <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">Facturada parcialmente</span>}
              {!pend && !crt.vinculacion_facturas_cerrada && <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1"><BadgeCheck size={10} /> Facturada</span>}
              {crt.vinculacion_facturas_cerrada && <BadgeCierre doc={crt} testId={`svc-vinc-cerrada-${crt._id}`} />}{/* Fix GS */}
            </div>
            <p className="text-xs text-gray-600 mt-0.5">{crt.project_name} · <span className="font-medium">{crt.provider_name}</span></p>
            {crt.alquiler_period_from && <p className="text-[11px] text-orange-600 mt-0.5">Período: {formatDate(crt.alquiler_period_from)} – {formatDate(crt.alquiler_period_to)}</p>}
            {crt.anticipo && <p className="text-[11px] text-violet-600 mt-0.5 font-semibold">{crt.categoria === "alquiler" ? "Anticipo de alquiler" : "Anticipo"}</p>}   {/* Fix FQ (2026-10-12) · Fix FV */}
            <p className="text-sm font-bold text-[#2E6DA4] mt-0.5">{formatPYG(crt.total || 0)} <span className="text-[10px] text-gray-400 font-normal">certificado c/IVA</span></p>
            {invs.length > 0 && (
              <p className="text-xs text-emerald-600 font-medium mt-0.5" data-testid={`svc-invoice-line-${crt._id}`}>
                Factura{invs.length > 1 ? "s" : ""}: {invs.map(i => i.invoice_number).join(", ")} · {formatPYG(cov.invoiced_total)} de {formatPYG(cov.crt_total)}
              </p>
            )}
            {cov.pending_total > 1 && !crt.vinculacion_facturas_cerrada && (
              <p className="text-xs text-amber-700 font-bold mt-0.5" data-testid={`svc-falta-facturar-${crt._id}`}>Falta facturar {formatPYG(cov.pending_total)}</p>
            )}
            {cov.over && <p className="text-xs text-red-600 font-bold mt-0.5">Lo facturado supera lo certificado en {formatPYG(cov.invoiced_total - cov.crt_total)}</p>}
            <VinculacionCerradaInfo doc={crt} testId={`svc-vinc-motivo-${crt._id}`} />{/* Fix GS */}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
            {pend && canUnlink && invs.length > 0 && (
              <button onClick={() => vinculacion(crt, true)} disabled={busyId === crt._id}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 whitespace-nowrap disabled:opacity-50" data-testid={`svc-cerrar-vinc-${crt._id}`}>
                <Lock size={13} /> Cerrar vinculación
              </button>
            )}
            {!pend && canUnlink && crt.vinculacion_facturas_cerrada && (
              <button onClick={() => vinculacion(crt, false)} disabled={busyId === crt._id}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50 whitespace-nowrap disabled:opacity-50">
                Reabrir vinculación
              </button>
            )}
            {canMatch && (
              <button onClick={() => openModal(crt)}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap ${pend ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border border-emerald-200 text-emerald-700 hover:bg-emerald-50"}`}
                data-testid={`svc-facturas-btn-${crt._id}`}>
                <Receipt size={13} /> {invs.length > 0 ? "Agregar / Ver facturas" : "Vincular factura"}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="bg-emerald-50/70 rounded-xl border border-emerald-100 p-4 text-xs text-emerald-800">
        <p className="font-semibold mb-1">Facturas de Servicios y Alquileres</p>
        <p>Cada certificación (la del servicio y la de cada período del alquiler) lleva sus facturas. Subí una o varias: la IA lee el N°, el timbrado, la fecha y el total, y se valida contra lo certificado (con IVA incluido).</p>
      </div>
      {/* Fix FV (2026-10-13): la barra de filtros de Materiales. */}
      <BarraFiltrosServicios fl={fl} filtros={filtros} testIdPrefix="svc-facturas" searchPlaceholder="Buscar por N° CRT, OTS, obra o proveedor..."
        statusOptions={fl.opciones(vigentes, { ref: refOTS, estado: estadoFactura, etiquetas: { pendiente: "Pendientes de factura", vinculada: "Facturadas" }, fijos: ["pendiente", "vinculada"] })} />

      {loading ? (
        <div className="space-y-3">{[1, 2].map(i => <Skeleton key={i} className="h-16" />)}</div>
      ) : pendientes.length === 0 && facturadas.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200/60 p-12 text-center">
          <Receipt size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No hay certificaciones para facturar</p>
          <p className="text-xs text-gray-400 mt-1">Primero certificá las OTS en la pestaña Certificación</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pendientes.length > 0 && (
            <div><h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Pendientes / Parcialmente facturadas ({pendientes.length})</h3>
              <div className="space-y-3">{pendientes.map(c => card(c, true))}</div></div>
          )}
          {facturadas.length > 0 && (
            <div><h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Facturadas ({facturadas.length})</h3>
              <div className="space-y-3">{facturadas.map(c => card(c, false))}</div></div>
          )}
        </div>
      )}

      {cerrarCRT && (   /* Fix GS */
        <CerrarVinculacionModal doc={cerrarCRT} etiqueta={`La certificación ${cerrarCRT.number}`}
          total={crtCoverage(cerrarCRT).crt_total} facturado={crtCoverage(cerrarCRT).invoiced_total}
          onConfirm={(motivo) => vinculacion(cerrarCRT, true, motivo)} onClose={() => setCerrarCRT(null)} />
      )}
      {modal && (
        <Modal open={!!modal} onClose={() => setModal(null)} title={`Facturas de ${modal.number} · OTS ${modal.ots_number}`} extraWide>
          <div className="space-y-5" data-testid="svc-facturas-modal">
            <input ref={filesRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={onFiles} data-testid="svc-facturas-files" />
            <input ref={attachRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onAttach} />
            <div className="bg-[#2E6DA4]/8 rounded-xl border border-[#2E6DA4]/20 p-4 flex flex-wrap gap-4 text-xs text-gray-700">
              <span><span className="font-semibold">Proveedor:</span> {modal.provider_name}</span>
              <span><span className="font-semibold">Certificado (IVA incluido):</span> {formatPYG(modal.total || 0)}</span>
              <span><span className="font-semibold">Proyecto:</span> {modal.project_name}</span>
              {modal.alquiler_period_from && <span><span className="font-semibold">Período:</span> {formatDate(modal.alquiler_period_from)} – {formatDate(modal.alquiler_period_to)}</span>}
            </div>

            <div className="rounded-xl border border-gray-200 overflow-hidden" data-testid="svc-coverage-panel">
              <div className="bg-slate-800 text-white px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs font-bold uppercase tracking-wide">Validación de facturación</p>
                <p className="text-xs font-semibold" data-testid="svc-coverage-summary">Facturado {formatPYG(live.invoiced_total)} de {formatPYG(live.crt_total)} · {live.invoices_count} factura{live.invoices_count === 1 ? "" : "s"}</p>
              </div>
              {live.pending_total > 1 && !modal.vinculacion_facturas_cerrada && <div className="px-4 py-1.5 bg-amber-50 text-[11px] font-bold text-amber-800" data-testid="svc-coverage-falta">Falta facturar {formatPYG(live.pending_total)}</div>}
              {modal.vinculacion_facturas_cerrada && <div className="px-4 pb-2"><VinculacionCerradaInfo doc={modal} testId="svc-modal-vinc-cerrada" /></div>}{/* Fix GS */}
              {live.over && (
                <div className="px-4 py-2 bg-amber-50 border-t border-amber-200 text-[11px] text-amber-800 flex items-start gap-2" data-testid="svc-coverage-over">
                  <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                  <span>La suma de las facturas ({formatPYG(live.invoiced_total)}) supera lo certificado ({formatPYG(live.crt_total)}). Podés continuar igual: es solo una advertencia.</span>
                </div>
              )}
              {!live.over && live.pending_total <= 1 && live.invoices_count > 0 && <div className="px-4 py-1.5 bg-emerald-50 text-[11px] font-bold text-emerald-800" data-testid="svc-coverage-ok">Las facturas cubren lo certificado</div>}
            </div>

            {linked.length > 0 && (
              <div className="rounded-xl border border-emerald-200 overflow-hidden" data-testid="svc-linked-invoices">
                <div className="bg-emerald-50 px-4 py-2 border-b border-emerald-200"><p className="text-xs font-bold text-emerald-800 uppercase tracking-wide">Facturas ya vinculadas ({linked.length})</p></div>
                <div className="divide-y divide-emerald-100">
                  {linked.map((inv, i) => (
                    <div key={inv.invoice_id || i} className="px-4 py-2 flex items-center justify-between gap-3 text-xs" data-testid={`svc-linked-invoice-${i}`}>
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-800 font-mono">{inv.invoice_number}</p>
                        <p className="text-[11px] text-gray-500">
                          {inv.invoice_date ? formatDate(inv.invoice_date) : "sin fecha"}{inv.timbrado ? ` · Timbrado ${inv.timbrado}` : ""}{inv.linked_by_name ? ` · ${inv.linked_by_name}` : ""}
                        </p>
                        {inv.doc_available
                          ? <p className="text-[11px] text-emerald-700 flex items-center gap-1 mt-0.5"><Paperclip size={10} /> {inv.doc_name || "Documento"}</p>
                          : <p className="text-[11px] text-gray-400 italic flex items-center gap-1 mt-0.5"><AlertTriangle size={10} /> Documento no disponible</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className="font-bold text-gray-800">{formatPYG(inv.invoice_total || 0)}</span>
                        {inv.doc_available ? (
                          <>
                            <button onClick={() => window.open(docUrl(modal._id, inv.invoice_id, true), "_blank")} className="flex items-center gap-1 text-[11px] text-[#2E6DA4] border border-[#2E6DA4]/30 hover:bg-[#2E6DA4]/10 rounded-lg px-2 py-1" data-testid={`svc-view-doc-${i}`}><Eye size={11} /> Ver</button>
                            <button onClick={() => window.open(docUrl(modal._id, inv.invoice_id, false), "_blank")} className="flex items-center gap-1 text-[11px] text-gray-600 border border-gray-300 hover:bg-gray-50 rounded-lg px-2 py-1"><Download size={11} /> Descargar</button>
                          </>
                        ) : canMatch && inv.invoice_id !== "legacy-1" && (
                          <button onClick={() => { setAttachingId(inv.invoice_id); attachRef.current.click(); }} disabled={attachingId === inv.invoice_id}
                            className="flex items-center gap-1 text-[11px] text-gray-500 border border-dashed border-gray-300 hover:bg-gray-50 rounded-lg px-2 py-1 disabled:opacity-50" data-testid={`svc-attach-doc-${i}`}>
                            {attachingId === inv.invoice_id ? <Loader2 size={11} className="animate-spin" /> : <UploadCloud size={11} />} Adjuntar
                          </button>
                        )}
                        {canUnlink && (
                          <button onClick={() => desvincular(inv)} disabled={busyId === inv.invoice_id}
                            className="flex items-center gap-1 text-[11px] text-red-600 border border-red-200 hover:bg-red-50 rounded-lg px-2 py-1 disabled:opacity-50" data-testid={`svc-unlink-${i}`}>
                            {busyId === inv.invoice_id ? <Loader2 size={11} className="animate-spin" /> : <Unlink size={11} />} Desvincular
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-3">
              <p className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5"><Zap size={12} className="text-emerald-600" /> Cargar y analizar facturas con IA</p>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => filesRef.current?.click()} className="flex items-center gap-1.5 text-xs bg-white border border-emerald-300 text-emerald-700 px-3 py-2 rounded-xl font-semibold hover:bg-emerald-50" data-testid="svc-select-files-btn"><FileUp size={13} /> Seleccionar facturas (varias)</button>
                <button onClick={() => setDrafts(p => [...p, newDraft()])} className="flex items-center gap-1.5 text-xs bg-white border border-gray-300 text-gray-600 px-3 py-2 rounded-xl font-semibold hover:bg-gray-50" data-testid="svc-manual-invoice-btn"><Plus size={13} /> Factura manual</button>
                <button onClick={analizar} disabled={analyzing || !drafts.some(d => d.file && !d.analyzed)} className="flex items-center gap-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 py-2 rounded-xl font-semibold" data-testid="svc-ai-extract-btn">
                  {analyzing ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />}{analyzing ? "Analizando..." : `Analizar con IA (${drafts.filter(d => d.file && !d.analyzed).length})`}
                </button>
              </div>
              <p className="text-[10px] text-emerald-700 mt-1.5">Máx. {MAX_FILE_MB}MB por archivo. La IA extrae N° de factura, timbrado, fecha y total.</p>
            </div>

            {drafts.length > 0 && (
              <div className="space-y-3">
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wide">Facturas a vincular ({drafts.length})</p>
                {drafts.map((d, i) => (
                  <DraftCard key={d.key} draft={d} index={i}
                    onChange={(nd) => setDrafts(p => p.map(x => x.key === d.key ? nd : x))}
                    onRemove={() => setDrafts(p => p.filter(x => x.key !== d.key))} />
                ))}
              </div>
            )}

            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <button onClick={() => setModal(null)} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cerrar</button>
              <button onClick={vincular} disabled={linking || !drafts.some(d => (d.invoice_number || "").trim())}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="svc-confirm-link-btn">
                {linking ? <Loader2 size={15} className="animate-spin" /> : <BadgeCheck size={15} />}
                {linking ? "Vinculando..." : drafts.length > 1 ? `Confirmar y Vincular ${drafts.length} Facturas` : "Confirmar y Vincular Factura"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
